import { DATAGRID_ROWS_PER_PAGE_OPTIONS } from '@ingenico/react-ui/datagrid';
import type { GridColumnVisibilityModel, GridSortModel, GridInitialState } from '@mui/x-data-grid-premium';
import { computeDeploymentUnitStatusColor } from '../../domain/api/mappers';
import type {
  EnumDeploymentUnitStatus,
  SearchFilter,
  SearchRequestSort,
} from '../../domain/models/deployment-unit.model';
import { searchFilterFilterType, searchOperator } from '../../domain/models/deployment-unit.model';
import type { TransformFiltersParams } from './DeploymentTargetDevicesTab.model';

export function getField(field: string): string {
  switch (field) {
    case 'modelAndManufacturer': {
      return 'deploymentUnit.model';
    }
    case 'serialAndPartNumber': {
      return 'deploymentUnit.serialNumber';
    }
    case 'type': {
      return 'deploymentUnit.options.type';
    }
    default: {
      return `deploymentUnit.${field}`;
    }
  }
}

export function transformSortModelForRequest(gridSort: GridSortModel): SearchRequestSort {
  const [currentSort] = gridSort;
  return currentSort?.sort
    ? {
        field: getField(currentSort.field),
        order: currentSort.sort === 'asc' ? 'ASC' : 'DESC',
      }
    : transformSortModelForRequest(defaultDeploymentTargetDevicesSortModel);
}

export const defaultDeploymentTargetDevicesSortModel: GridSortModel = [
  {
    field: 'creationDate',
    sort: 'desc',
  },
];

export const deploymentTargetDevicesInitialState: GridInitialState = {
  pagination: { paginationModel: { pageSize: DATAGRID_ROWS_PER_PAGE_OPTIONS[0], page: 0 } },
};

export function transformFiltersToRequestCall({
  selectedQuickFilterStatusValue,
  searchQuery,
  deploymentId,
  logicalDeviceId,
  isOpenedFromLogicalDevice,
  selectedConfigProfileId,
  endDate,
  startDate,
}: TransformFiltersParams): SearchFilter[] {
  const quickFilter =
    selectedQuickFilterStatusValue && selectedQuickFilterStatusValue.length > 0
      ? [
          {
            filterType: searchFilterFilterType.array,
            name: 'deploymentUnit.status',
            operator: searchOperator.in,
            value: JSON.stringify([selectedQuickFilterStatusValue]),
          },
        ]
      : [];

  const searchFilter = searchQuery
    ? [
        {
          filterType: searchFilterFilterType.fulltext,
          name: '',
          operator: searchOperator.equals,
          value: `*${searchQuery}*`,
        },
      ]
    : [];

  const deploymentIdFilter = deploymentId
    ? [
        {
          filterType: searchFilterFilterType.string,
          name: 'deploymentUnit.deploymentRequestId',
          operator: searchOperator.equals,
          value: deploymentId,
        },
      ]
    : [];

  const deviceIdFilter =
    isOpenedFromLogicalDevice && logicalDeviceId
      ? [
          {
            filterType: searchFilterFilterType.string,
            name: 'deploymentUnit.deviceId',
            operator: searchOperator.equals,
            value: logicalDeviceId,
          },
        ]
      : [];

  const selectedConfigProfileFilter =
    selectedConfigProfileId && selectedConfigProfileId.length > 0
      ? [
          {
            filterType: searchFilterFilterType.array,
            name: 'deploymentUnit.configuration.id',
            operator: searchOperator.in,
            value: JSON.stringify([selectedConfigProfileId]),
          },
        ]
      : [];

  const startDateFilter =
    startDate && isOpenedFromLogicalDevice
      ? [
          {
            filterType: searchFilterFilterType.string,
            name: 'deploymentUnit.creationDate',
            operator: searchOperator.greaterThan,
            value: new Date(startDate).toISOString(),
          },
        ]
      : [];
  const endDateFilter =
    endDate && isOpenedFromLogicalDevice
      ? [
          {
            filterType: searchFilterFilterType.string,
            name: 'deploymentUnit.creationDate',
            operator: searchOperator.lowerThan,
            value: new Date(endDate).toISOString(),
          },
        ]
      : [];

  return [
    ...deploymentIdFilter,
    ...quickFilter,
    ...searchFilter,
    ...deviceIdFilter,
    ...selectedConfigProfileFilter,
    ...startDateFilter,
    ...endDateFilter,
  ];
}

export function getInitialColumnVisibilityModel(
  availableColumns: string[],
  overriden?: GridColumnVisibilityModel,
): GridColumnVisibilityModel {
  return {
    ...availableColumns.reduce(
      (accum, current) => ({
        ...accum,
        [current]: true,
      }),
      {},
    ),
    ...overriden,
  };
}

export function getDeploymentTargetDevicesChipStatusColor(status?: EnumDeploymentUnitStatus): string {
  return computeDeploymentUnitStatusColor(status);
}
